import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Clock, LogOut } from 'lucide-react';
import { getLogger } from '@/utils/logger';

const log = getLogger('SessionExpiryWarningModal');
const WARNING_WINDOW_SECONDS = 2 * 60;

const SessionExpiryWarningModal = ({
  timeUntilExpiry,
  onDismiss,
  onLogout,
  onExtendSession,
}) => {
  const initialTimeUntilExpiry = typeof timeUntilExpiry === 'number' ? timeUntilExpiry : 0;
  const [countdown, setCountdown] = useState(Math.max(0, Math.floor(initialTimeUntilExpiry / 1000)));
  const [expiresAtMs, setExpiresAtMs] = useState(Date.now() + Math.max(0, initialTimeUntilExpiry));
  const [isExtending, setIsExtending] = useState(false);

  // Keep local countdown aligned to latest token expiry.
  useEffect(() => {
    if (typeof timeUntilExpiry === 'number') {
      const safeTimeUntilExpiry = Math.max(0, timeUntilExpiry);
      setExpiresAtMs(Date.now() + safeTimeUntilExpiry);
      setCountdown(Math.floor(safeTimeUntilExpiry / 1000));
    }
  }, [timeUntilExpiry]);

  // Update countdown every second from absolute expiry timestamp.
  useEffect(() => {
    const timer = setInterval(() => {
      const secondsLeft = Math.max(0, Math.floor((expiresAtMs - Date.now()) / 1000));
      setCountdown(secondsLeft);
    }, 1000);

    return () => clearInterval(timer);
  }, [expiresAtMs]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progressValue = useMemo(() => {
    const rawValue = ((WARNING_WINDOW_SECONDS - countdown) / WARNING_WINDOW_SECONDS) * 100;
    return Math.max(0, Math.min(100, rawValue));
  }, [countdown]);

  const handleExtendSession = async () => {
    if (!onExtendSession || isExtending) {
      return;
    }

    setIsExtending(true);
    try {
      await onExtendSession();
    } catch (error) {
      log.error('[SessionExpiryWarningModal] Extend session failed', error);
    } finally {
      setIsExtending(false);
    }
  };

  const handleLogout = async () => {
    log.info('[SessionExpiryWarningModal] Account chose to logout');
    await onLogout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onDismiss()}>
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-600" />
            <DialogTitle className="text-amber-800">Session Expiring Soon</DialogTitle>
          </div>
          <DialogDescription className="text-amber-700 mt-2">
            Your session expires in <span className="font-bold">{formatTime(countdown)}</span>.
            <br />
            <span className="text-sm text-amber-600 mt-2 block">
              Select <strong>Extend session</strong> to renew your access token.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <Progress value={progressValue} className="h-2 bg-amber-100" />
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button
            onClick={handleExtendSession}
            className="bg-[#3f4f24] hover:bg-[#27423e] flex-1"
            id="session-expiry-warning-btn-extend"
            disabled={isExtending}
          >
            {isExtending ? 'Extending...' : 'Extend session'}
          </Button>

          <Button
            variant="outline"
            onClick={onDismiss}
            className="border-slate-300 hover:bg-slate-50 flex-1"
            id="session-expiry-warning-btn-dismiss"
            disabled={isExtending}
          >
            Dismiss
          </Button>

          <Button
            variant="outline"
            onClick={handleLogout}
            className="border-red-300 hover:bg-red-50 flex-1"
            id="session-expiry-warning-btn-logout"
            disabled={isExtending}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Logout
          </Button>
        </DialogFooter>

        <div className="text-xs text-center text-slate-500 mt-2">
          This warning will reappear soon if the token is still near expiry.
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SessionExpiryWarningModal;
